import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useSkeleton from "../../../../hooks/useSkeleton";

import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Password } from "primereact/password";
import { Checkbox } from "primereact/checkbox";
import { Button } from "primereact/button";
import { classNames } from "primereact/utils";

import "react-toastify/dist/ReactToastify.css";

import { atom, useAtom } from "jotai";
import { loadingState } from "../../../../states/loadingState";

const UsersForm = ({ settings }) => {
  const [isLoading, setIsLoading] = useAtom(loadingState);

  const s = useSkeleton("abm");
  const { t } = useTranslation();

  const isEditMode = settings.formMode === "UPDATE";
  const isDeleteMode = settings.formMode === "DELETE";

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      created: 0,
      nick: "",
      email: "",
      salt: "",
      passwordHash: "",
    },
  });

  let passwordMode = "HIDDEN"; // "UPDATE", "VISIBLE"

  const formModeConfig = {};

  if (isEditMode) {
    formModeConfig.SubmitButtonText = t("buttons.update");
    formModeConfig.SubmitButtonTextWait = `${t("buttons.updating")}...`;
    formModeConfig.serverAction = async (data) => {
      await settings.CRUDService.updateEntity(data);
    };
  } else if (isDeleteMode) {
    formModeConfig.SubmitButtonText = t("buttons.delete");
    formModeConfig.SubmitButtonTextWait = `${t("buttons.deleting")}...`;
    formModeConfig.serverAction = async () => {
      await settings.CRUDService.deleteEntity(settings.entityId);
    };
  } else {
    formModeConfig.SubmitButtonText = t("buttons.create");
    formModeConfig.SubmitButtonTextWait = `${t("buttons.creating")}...`;
    formModeConfig.serverAction = async (data) => {
      await settings.CRUDService.createEntity(data);
    };
  }

  useEffect(() => {
    if (isEditMode || isDeleteMode) {
      const fetchData = async () => {
        try {
          const data = await settings.CRUDService.getEntity(settings.entityId);

          for (const key in data) {
            setValue(key, data[key]);
          }
        } catch (error) {
          console.error(t("forms.messages.operation.error"), error);
        }
      };

      fetchData();
    }
  }, [settings.entityId, setValue, settings.formMode]);

  const handlePassword = (e) => {
    e.preventDefault();
    passwordMode = "VISIBLE";
  };

  const [isVisible, setIsVisible] = useState(passwordMode == "VISIBLE");

  const handleShow = (e) => {
    e.preventDefault();
    setIsVisible(!isVisible);
  };

  const handleHide = () => {
    setIsVisible(false);
  };

  const onSubmit = async (data) => {
    setIsLoading(true);

    try {
      await formModeConfig.serverAction(data);

      settings.hideDialog();

      settings.toast.current.show({
        severity: "success",
        summary: t("forms.messages.titles.success"),
        detail: t("forms.messages.operation.success"),
        life: 3000,
      });
    } catch (error) {
      let summary = t("forms.messages.titles.error");
      let detail = t("forms.messages.operation.error");
      console.log(error);
      if (error.message == "USERS_EXCEPTION__DUPLICATED_MAIL") {
        summary = "MAIL DUPLICADO";
        detail = "YA EXISTE UN USUARIO CON EL MAIL INGRESADO";
      }
      settings.toast.current.show({
        severity: "error",
        summary,
        detail,
        life: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getFormErrorMessage = (error) => {
    return error && <small className="p-error">{error.message}</small>;
  };

  return (
    <React.Fragment>
      <div className={s.get()}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className={`${s.get("form")} ${settings.formMode !== "DELETE" ? "abm-create-form" : ""}`}
        >
          <Controller
            name="created"
            control={control}
            rules={{
              required: t("forms.validation.required", {
                entity: t("entities.users.created"),
              }),
            }}
            render={({ field }) => (
              <div className={s.get("form.field")}>
                <div className={s.get("form.field.label")}>
                  <label htmlFor="created">{t("entities.users.created")}</label>
                </div>
                <div className={s.get("form.field.value")}>
                  <div className={s.get("form.field.value.error")}>
                    {getFormErrorMessage(errors.created)}
                  </div>
                  <div className={s.get("form.field.value.control")}>
                    <InputNumber
                      id={field.name}
                      value={field.value || 0}
                      onValueChange={(e) => field.onChange(e.value)}
                      mode="decimal"
                      locale="de-DE"
                      minFractionDigits={2}
                      min={-90}
                      max={90}
                      inputClassName={[
                        classNames({ "p-invalid": errors.created }),
                      ]}
                      disabled={settings.formMode == "DELETE"}
                    />
                  </div>
                </div>
              </div>
            )}
          />
          <Controller
            name="nick"
            control={control}
            rules={{
              required: t("forms.validation.required", {
                entity: t("entities.users.nick"),
              }),
              maxLength: {
                value: 30,
                message: t("forms.validation.maxLength", {
                  entity: t("entities.users.nick"),
                  maxLength: 30,
                }),
              },
            }}
            render={({ field, fieldState }) => (
              <div className={s.get("form.field")}>
                <div className={s.get("form.field.label")}>
                  <label htmlFor="nick">{t("entities.users.nick")}</label>
                </div>
                <div className={s.get("form.field.value")}>
                  <div className={s.get("form.field.value.error")}>
                    {getFormErrorMessage(errors.nick)}
                  </div>
                  <div className={s.get("form.field.value.control")}>
                    <InputText
                      id="nick"
                      {...field}
                      value={field.value}
                      maxLength={100}
                      className={[
                        "form-control",
                        classNames({
                          "p-invalid": fieldState.invalid,
                        }),
                      ]}
                      disabled={settings.formMode == "DELETE"}
                    />
                  </div>
                </div>
              </div>
            )}
          />
          <Controller
            name="email"
            control={control}
            rules={{
              maxLength: {
                value: 60,
                message: t("forms.validation.maxLength", {
                  entity: t("entities.users.email"),
                  maxLength: 60,
                }),
              },
            }}
            render={({ field, fieldState }) => (
              <div className={s.get("form.field")}>
                <div className={s.get("form.field.label")}>
                  <label htmlFor="email">{t("entities.users.email")}</label>
                </div>
                <div className={s.get("form.field.value")}>
                  <div className={s.get("form.field.value.error")}>
                    {getFormErrorMessage(errors.email)}
                  </div>
                  <div className={s.get("form.field.value.control")}>
                    <InputText
                      id="email"
                      {...field}
                      value={field.value}
                      maxLength={100}
                      className={[
                        "form-control",
                        classNames({
                          "p-invalid": fieldState.invalid,
                        }),
                      ]}
                      disabled={settings.formMode == "DELETE"}
                    />
                  </div>
                </div>
              </div>
            )}
          />
          <Controller
            name="salt"
            control={control}
            rules={{
              required: t("forms.validation.required", {
                entity: t("entities.users.salt"),
              }),
              maxLength: {
                value: 50,
                message: t("forms.validation.maxLength", {
                  entity: t("entities.users.salt"),
                  maxLength: 50,
                }),
              },
            }}
            render={({ field, fieldState }) => (
              <div className={s.get("form.field")}>
                <div className={s.get("form.field.label")}>
                  <label htmlFor="salt">{t("entities.users.salt")}</label>
                </div>
                <div className={s.get("form.field.value")}>
                  <div className={s.get("form.field.value.error")}>
                    {getFormErrorMessage(errors.salt)}
                  </div>
                  <div className={s.get("form.field.value.control")}>
                    <InputText
                      id="salt"
                      {...field}
                      value={field.value}
                      maxLength={100}
                      className={[
                        "form-control",
                        classNames({
                          "p-invalid": fieldState.invalid,
                        }),
                      ]}
                      disabled={settings.formMode == "DELETE"}
                    />
                  </div>
                </div>
              </div>
            )}
          />
          <Controller
            name="passwordHash"
            control={control}
            rules={{
              required: t("forms.validation.required", {
                entity: t("entities.users.passwordHash"),
              }),
              maxLength: {
                value: 100,
                message: t("forms.validation.maxLength", {
                  entity: t("entities.users.passwordHash"),
                  maxLength: 100,
                }),
              },
            }}
            render={({ field, fieldState }) => (
              <div className={s.get("form.field")}>
                <div className={s.get("form.field.label")}>
                  <label htmlFor="passwordHash">
                    {t("entities.users.passwordHash")}
                  </label>
                </div>
                <div className={s.get("form.field.value")}>
                  <div className={s.get("form.field.value.error")}>
                    {getFormErrorMessage(errors.passwordHash)}
                  </div>
                  <div className={s.get("form.field.value.control")}>
                    <InputText
                      id="passwordHash"
                      {...field}
                      value={field.value}
                      maxLength={100}
                      className={[
                        "form-control",
                        classNames({
                          "p-invalid": fieldState.invalid,
                        }),
                      ]}
                      disabled={settings.formMode == "DELETE"}
                    />
                  </div>
                </div>
              </div>
            )}
          />

          <div className="flex justify-content-between mt-6 mb-2">
            <Button
              label={t("buttons.cancel")}
              icon="pi pi-times"
              className="p-button-text btn-secondary-crud"
              onClick={() => settings.hideDialog("CANCEL")}
              type="button"
              disabled={isLoading}
            />
            <Button
              label={
                isLoading
                  ? formModeConfig.SubmitButtonTextWait
                  : formModeConfig.SubmitButtonText
              }
              icon="pi pi-check"
              className=" btn btn-primary btn-primary-crud"
              type="submit"
              disabled={isLoading}
            />
          </div>
        </form>
      </div>
    </React.Fragment>
  );
};

export default UsersForm;
